'use client';

import { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';

const ddApplicationId = process.env.ddApplicationId ?? '';
const ddClientToken = process.env.ddClientToken ?? '';
const isProduction = process.env.stage === 'prod';
const thirdPartyPatterns = [
  'snap.licdn.com',
  'li.lms-analytics',
  'googletagmanager.com',
  'google-analytics.com',
  'doubleclick.net',
  'facebook.net',
];

const blockedErrorSubstrings = [
  'chrome-extension://',
  'moz-extension://',
  'swbrowser',
  'xbrowser',
  'Script error.',
];

type RumResourceEvent = {
  type: 'resource';
  resource?: {
    url?: string;
  };
};

type RumErrorEvent = {
  type: 'error';
  error?: {
    resource?: {
      url?: string;
    };
  };
};

type RumEvent = RumResourceEvent | RumErrorEvent | { type: string };

function getEventUrl(event: RumEvent): string {
  if (event.type === 'resource' && 'resource' in event) {
    return event.resource?.url ?? '';
  }
  if (event.type === 'error' && 'error' in event) {
    return event.error?.resource?.url ?? '';
  }
  return '';
}

let isRumInitialized = false;

export default function DatadogInit(): null {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isProduction && !isRumInitialized) {
      datadogRum.init({
        applicationId: ddApplicationId,
        clientToken: ddClientToken,
        site: 'datadoghq.com',
        service: 'front-colectivos-pyme',
        env: 'prod',
        // Specify a version number to identify the deployed version of your application in Datadog
        // version: '1.0.0',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'allow',
        beforeSend: (event) => {
          if (event.type === 'error') {
            const errorEvent = event as { error?: { stack?: string }; message?: string };
            const stack = errorEvent.error?.stack ?? '';
            const message = errorEvent.message ?? '';
            if (
              blockedErrorSubstrings.some(
                (pattern) => stack.includes(pattern) || message.includes(pattern)
              )
            ) {
              return false;
            }
          }
          if (event.type === 'error' || event.type === 'resource') {
            const url = getEventUrl(event as RumEvent);
            if (url && thirdPartyPatterns.some((pattern) => url.includes(pattern))) {
              return false;
            }
          }
          return true;
        },
      });
      isRumInitialized = true;
    }
  }, []);
  return null;
}
